import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { conversationsAPI } from '../services/api';
import { getSocket } from '../services/socket';
import { getLocale } from '../utils/dateFormat';
import '../styles/MyMessages.css';

const MyMessages = () => {
  const { t } = useTranslation();
  const { user, socketConnected } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentConversationRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const openedConvRef = useRef(null);

  // === CALLBACKS (déclarés avant les useEffect) ===

  const scrollToBottom = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  };

  const fetchConversations = useCallback(async () => {
    try {
      const response = await conversationsAPI.getAll();
      setConversations(response.data);
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Version debounced pour les événements socket (évite les rafales de requêtes)
  const debouncedFetchConversations = useCallback(() => {
    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    fetchDebounceRef.current = setTimeout(() => {
      fetchConversations();
    }, 500);
  }, [fetchConversations]);

  const openConversation = useCallback(async (conversationId) => {
    try {
      setSelectedConversation(conversationId);
      const response = await conversationsAPI.getMessages(conversationId);
      setMessages(response.data.messages);
      setConversationInfo({
        product: response.data.product,
        otherUser: response.data.otherUser,
        isBuyer: response.data.isBuyer,
        type: response.data.type || 'product',
        subject: response.data.subject || null
      });

      // Marquer comme lu via socket
      const socket = getSocket();
      if (socket?.connected) {
        socket.emit('messages:read', { conversationId });
      }

      // Mettre à jour seulement le compteur non lu dans la liste existante
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (err) {
      console.error('Erreur chargement messages:', err);
    }
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    // Arrêter l'indicateur de frappe
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('typing:stop', selectedConversation);
    }

    try {
      // Envoyer via API
      const response = await conversationsAPI.sendMessage(selectedConversation, messageText);

      // Ajouter le message localement
      const newMsg = {
        ...response.data,
        sender_username: user.username
      };

      setMessages(prev => {
        // Éviter les doublons si le socket l'a déjà ajouté
        if (prev.some(m => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });

      debouncedFetchConversations();
    } catch (err) {
      console.error('Erreur envoi message:', err);
      // Restaurer le message en cas d'erreur
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);

    if (!selectedConversation) return;

    const socket = getSocket();
    if (!socket?.connected) return;

    // Démarrer l'indicateur de frappe
    socket.emit('typing:start', selectedConversation);

    // Arrêter après 2 secondes d'inactivité
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('typing:stop', selectedConversation);
    }, 2000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return t('messages.yesterday');
    } else if (days < 7) {
      return date.toLocaleDateString(getLocale(), { weekday: 'short' });
    }
    return date.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit' });
  };

  const formatMessageTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString(getLocale(), {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // === EFFECTS ===

  // Mettre à jour la ref quand la conversation change
  useEffect(() => {
    currentConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [user, navigate, fetchConversations]);

  // Socket listeners - se connecter quand le socket est prêt
  useEffect(() => {
    if (!socketConnected) return;

    const socket = getSocket();
    if (!socket) return;

    // Écouter les nouveaux messages
    const handleNewMessage = (msg) => {
      // Mettre à jour la liste des messages si on est dans cette conversation
      if (currentConversationRef.current === msg.conversation_id) {
        setMessages(prev => {
          // Éviter les doublons
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Marquer comme lu immédiatement
        socket.emit('messages:read', { conversationId: msg.conversation_id });
      }

      // Mettre à jour la liste des conversations (debounced)
      debouncedFetchConversations();
    };

    // Écouter l'indicateur de frappe
    const handleTypingSocket = (data) => {
      if (data.conversationId === currentConversationRef.current) {
        setTypingUsers(prev => ({
          ...prev,
          [data.userId]: data.isTyping
        }));
      }
    };

    // Écouter les messages marqués comme lus
    const handleMessagesRead = (data) => {
      if (data.conversationId === currentConversationRef.current) {
        setMessages(prev => prev.map(m => ({ ...m, is_read: 1 })));
      }
    };

    // Écouter les notifications de nouveaux messages
    const handleNewMessageNotification = (data) => {
      debouncedFetchConversations();
    };

    socket.on('message:received', handleNewMessage);
    socket.on('typing:update', handleTypingSocket);
    socket.on('messages:marked-read', handleMessagesRead);
    socket.on('notification:new-message', handleNewMessageNotification);

    return () => {
      socket.off('message:received', handleNewMessage);
      socket.off('typing:update', handleTypingSocket);
      socket.off('messages:marked-read', handleMessagesRead);
      socket.off('notification:new-message', handleNewMessageNotification);
    };
  }, [socketConnected, debouncedFetchConversations]);

  // Ouvrir automatiquement une conversation si passée en paramètre
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && conversations.length > 0 && openedConvRef.current !== parseInt(convId)) {
      openedConvRef.current = parseInt(convId);
      openConversation(parseInt(convId));
    }
  }, [searchParams, conversations, openConversation]);

  // Scroll vers le bas quand nouveaux messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Rejoindre/quitter la conversation via socket
  useEffect(() => {
    if (!socketConnected || !selectedConversation) return;

    const socket = getSocket();
    if (!socket) return;

    socket.emit('conversation:join', selectedConversation);

    return () => {
      socket.emit('conversation:leave', selectedConversation);
    };
  }, [selectedConversation, socketConnected]);

  // Vérifier si quelqu'un tape
  const isOtherUserTyping = Object.entries(typingUsers).some(
    ([id, isTyping]) => isTyping && parseInt(id) !== user?.userId
  );

  if (loading) {
    return (
      <div className="messages-page">
        <div className="loading-container">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-page">
      <div className="messages-container">
        {/* Liste des conversations */}
        <div className={`conversations-list ${selectedConversation ? 'hidden-mobile' : ''}`}>
          <div className="conversations-header">
            <h2>{t('messages.title')}</h2>
            {socketConnected && <span className="socket-status online" title="Temps réel actif">●</span>}
          </div>

          {conversations.length === 0 ? (
            <div className="no-conversations">
              <p>{t('messages.noConversations')}</p>
              <span>{t('messages.noConversationsHint')}</span>
            </div>
          ) : (
            <div className="conversations">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`conversation-item ${selectedConversation === conv.id ? 'active' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
                  onClick={() => openConversation(conv.id)}
                >
                  <div className="conv-image">
                    {conv.type === 'support' ? (
                      <div className="no-image support-icon">🛟</div>
                    ) : conv.product_image ? (
                      <img src={`/uploads/thumbnails/${conv.product_image}`} alt="" onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${conv.product_image}`; }} />
                    ) : (
                      <div className="no-image">📦</div>
                    )}
                  </div>
                  <div className="conv-info">
                    <div className="conv-header">
                      <span className="conv-username">{conv.other_username}</span>
                      <span className="conv-time">{formatDate(conv.last_message_at)}</span>
                    </div>
                    <div className="conv-product">
                      {conv.type === 'support' ? t('messages.supportLabel', { subject: conv.subject }) : conv.product_title}
                    </div>
                    <div className="conv-preview">
                      {conv.last_message?.substring(0, 50)}
                      {conv.last_message?.length > 50 ? '...' : ''}
                    </div>
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="unread-badge">{conv.unread_count}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Zone de conversation */}
        <div className={`conversation-view ${selectedConversation ? 'visible' : ''}`}>
          {selectedConversation && conversationInfo ? (
            <>
              {/* Header conversation */}
              <div className="conversation-header">
                <button className="btn-back" onClick={() => setSelectedConversation(null)}>
                  ←
                </button>
                <div className="header-info">
                  <span className="header-username">{conversationInfo.otherUser?.username}</span>
                  <span className="header-role">
                    {conversationInfo.type === 'support' ? t('messages.supportConv') : conversationInfo.isBuyer ? t('messages.sellerLabel') : t('messages.buyerLabel')}
                  </span>
                </div>
              </div>

              {/* Info produit ou support */}
              {conversationInfo.type === 'support' ? (
                <div className="support-banner">
                  <span className="support-banner-icon">🛟</span>
                  <div className="product-info">
                    <span className="product-title">{t('messages.supportLabel', { subject: conversationInfo.subject })}</span>
                    <span className="support-label">{t('messages.supportTitle')}</span>
                  </div>
                </div>
              ) : conversationInfo.product && (
                <div className="product-banner" onClick={() => navigate(`/product/${conversationInfo.product.id}`)}>
                  {conversationInfo.product.image && (
                    <img src={`/uploads/thumbnails/${conversationInfo.product.image}`} alt="" onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${conversationInfo.product.image}`; }} />
                  )}
                  <div className="product-info">
                    <span className="product-title">{conversationInfo.product.title}</span>
                    <span className="product-price">{parseFloat(conversationInfo.product.price).toFixed(2)} CHF</span>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="messages-list" ref={messagesListRef}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.sender_id === user.userId ? 'sent' : 'received'}`}
                  >
                    <div className="message-content">
                      <p>{msg.message}</p>
                      <span className="message-time">
                        {formatMessageTime(msg.created_at)}
                        {msg.sender_id === user.userId && (
                          <span className="message-status">
                            {msg.is_read === 1 || msg.is_read === true ? ' ✓✓' : ' ✓'}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
                {isOtherUserTyping && (
                  <div className="message received typing-indicator">
                    <div className="message-content">
                      <p className="typing-dots">
                        <span>.</span><span>.</span><span>.</span>
                      </p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} aria-hidden="true" />
              </div>

              {/* Input message */}
              <form className="message-input" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  placeholder={t('messages.placeholder')}
                  value={newMessage}
                  onChange={handleTyping}
                  disabled={sending}
                />
                <button type="submit" disabled={!newMessage.trim() || sending}>
                  {sending ? '...' : '→'}
                </button>
              </form>
            </>
          ) : (
            <div className="no-conversation-selected">
              <p>{t('messages.selectConversation')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyMessages;
